import {AfterViewInit, DestroyRef, Directive, ElementRef, Input} from '@angular/core';
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {standardStreng} from "felles-ng2/støttefunksjoner/strenger";

const debug = initDebugger('ToggleKlasseVedScrollDirektiv');

/**
 * Setter klasse på elementet direktivet anvendes på når elementet har vertikal scroll, dvs. når innholdet er høyere enn elementet.
 *
 * Forutsetter at elementet er et scrollområde via css (f.eks. overflow-y: auto) og har avgrenset høyde.
 *
 * Benytter DOM apiene ResizeObserver (endring i størrelse på elementet og dets direkte barn) og MutationObserver (barn som legges til eller fjernes).
 * ResizeObserver leverer etter layout og før tegning, slik at klassen er på plass før skjermbildet tegnes.
 * Klassen byttes kun når tilstanden faktisk endres, og uten change detection, for å begrense omtegninger når brukeren endrer størrelse på nettleseren.
 * Se https://developer.mozilla.org/en-US/docs/Web/API/ResizeObserver
 *
 * Bruk:
 * ```
 * <etElement toggleKlasseVedScroll>
 * alternativt kan ønsket klasse som skal legges til elementet ved scroll angis:
 * <etElement toggleKlasseVedScroll="en-css-klasse">
 * ```
 */
@Directive({
    selector: '[toggleKlasseVedScroll]',
    standalone: true
})
export class ToggleKlasseVedScrollDirektiv implements AfterViewInit {
    private static cssKlasse = 'har-scroll';

    @Input({alias: 'toggleKlasseVedScroll'}) scrollklasse: string = ''; //dersom ikke angitt via 'toggleKlasseVedScroll="en-css-klasse"' vil scrollklasse få verdien '', uavhengig av hva vi init den til her..

    constructor(private el: ElementRef<HTMLElement>, private destroyRef: DestroyRef) {
    }

    ngAfterViewInit(): void {
        toggleKlasseVedScroll(this.el.nativeElement, standardStreng(this.scrollklasse, ToggleKlasseVedScrollDirektiv.cssKlasse), this.destroyRef)
    }
}

/** Kaller vedScroll hver gang angitt element går fra å ikke ha vertikal scroll til å ha det, eller omvendt (samt første gang tilstanden er beregnet) */
export function vedScroll(element: HTMLElement, vedScroll: (harScroll: boolean) => void, destroyRef: DestroyRef) {
    if (typeof ResizeObserver === 'undefined') return; //f.eks. i jsdom (jest)

    let harScroll: boolean | undefined = undefined;

    function beregn() {
        //+1 for å tåle avrunding av desimalpiksler
        const nyHarScroll = element.scrollHeight > element.clientHeight + 1;
        if (nyHarScroll === harScroll) return;
        harScroll = nyHarScroll;
        vedScroll(nyHarScroll);
    }

    //elementet endrer høyde (f.eks. vindusstørrelse), eller barna endrer høyde (f.eks. listen vokser eller krymper)
    const størrelsesobservatør = new ResizeObserver(() => beregn());
    størrelsesobservatør.observe(element);
    Array.from(element.children).forEach(barn => størrelsesobservatør.observe(barn));

    //barn som kommer og går (f.eks. via @if) må også observeres
    const barneobservatør = new MutationObserver(mutasjoner => {
        mutasjoner.forEach(mutasjon => {
            mutasjon.addedNodes.forEach(node => { if (node instanceof Element) størrelsesobservatør.observe(node) });
            mutasjon.removedNodes.forEach(node => { if (node instanceof Element) størrelsesobservatør.unobserve(node) });
        });
        beregn();
    });
    barneobservatør.observe(element, {childList: true});

    //terminer observering når element blir terminert
    destroyRef.onDestroy(() => {
        størrelsesobservatør.disconnect();
        barneobservatør.disconnect();
    });
}

/** Legger til angitt cssKlasse når angitt element har vertikal scroll, og fjerner den når elementet ikke lenger har det.
 * Som hovedregel bør kall til toggleKlasseVedScroll(..) legges i {@link AfterViewInit} */
export function toggleKlasseVedScroll(element: HTMLElement, cssKlasse: string, destroyRef: DestroyRef) {

    function scrollEndret(harScroll: boolean) {
        debug('scrollEndret %s "%s"', harScroll, cssKlasse)
        element.classList.toggle(cssKlasse, harScroll);
    }

    vedScroll(element, scrollEndret, destroyRef);
}
